import { WalletTypes } from '@/constants/general';
import { Nullable } from '@/types/common';
import { InferType, array, boolean, mixed, number, object } from 'yup';
import { safeString } from './common';

export const portalWhitelistRequestSchema = (isBankInfo) =>
    object({
        entityId: object({ label: safeString(), value: number() }).required('Please select an account.'),
        friendlyName: safeString().trim().required('Please input a name.'),
        baseCurrencyId: object({ label: safeString(), value: number() }).test(
            'is-required-when-whitelist-bank-info',
            `Please select a currency`,
            function (baseCurrency) {
                if (isBankInfo) {
                    return baseCurrency && baseCurrency.value !== undefined;
                }
                return true;
            }
        ),
        bankName: safeString()
            .trim()
            .when([], {
                is: () => isBankInfo,
                then: (schema) => schema.required('Please input a bank name.')
            }),
        bankAddress: safeString()
            .trim()
            .when([], {
                is: () => isBankInfo,
                then: (schema) => schema.required('Please input a bank address.')
            }),
        country: object({ label: safeString(), value: safeString() }).when('requestType', {
            is: () => isBankInfo,
            then: (schema) => schema.test('country', 'Please select a country.', (option) => !!option?.value),
            otherwise: (schema) => schema.optional()
        }),
        beneficiaryName: safeString().trim().nullable(),
        swiftCode: safeString()
            .nullable()
            .test('swift-xor-additional', function (value) {
                const { additionalAccountNumber, baseCurrencyId, requestType } = this.parent;

                if (!isBankInfo) return true;

                const isUSD = baseCurrencyId?.label === 'USD';

                const hasSwift = !!value?.trim();
                const hasAdditional = !!additionalAccountNumber?.trim();

                // Non-USD: SWIFT is mandatory
                if (!isUSD) {
                    if (!hasSwift) {
                        return this.createError({
                            message: 'SWIFT/BIC is required'
                        });
                    }
                    return true;
                }

                // USD: either SWIFT or ABA, but not both
                if (hasSwift && hasAdditional) {
                    return this.createError({
                        message: 'Please input either SWIFT/BIC or ABA Routing Number (not both)'
                    });
                }

                if (!hasSwift && !hasAdditional) {
                    return this.createError({
                        message: 'Please input either SWIFT/BIC or ABA Routing Number'
                    });
                }

                return true;
            }),
        ibanCode: safeString()
            .trim()
            .nullable()
            .when([], {
                is: () => isBankInfo,
                then: (schema) =>
                    schema.test(
                        'hasIbanOrAccount',
                        'Please provide either an IBAN Code or Account Number',
                        function (value) {
                            const accountNumber = this.parent.accountNumber;
                            return !!value || !!accountNumber;
                        }
                    )
            }),
        accountNumber: safeString()
            .trim()
            .nullable()
            .when([], {
                is: () => isBankInfo,
                then: (schema) =>
                    schema.test(
                        'hasIbanOrAccount',
                        'Please provide either an IBAN Code or Account Number',
                        function (value) {
                            const ibanCode = this.parent.ibanCode;
                            return !!value || !!ibanCode;
                        }
                    )
            }),
        additionalAccountNumber: safeString()
            .nullable()
            .test('additional-xor-swift', function (value) {
                const { swiftCode, baseCurrencyId, requestType, accountNumber } = this.parent;

                if (!isBankInfo) return true;

                // hasAdditional is true where there is additional account number
                const hasAdditional = !!value?.trim();

                const isUSD = baseCurrencyId?.label === 'USD';
                const isDkk = baseCurrencyId?.label === 'DKK';

                // For DKK, if there is accountNumber, then additional account number is required as well as the swift code.

                const hasAccountNumber = !!accountNumber?.trim();

                if (isDkk && hasAccountNumber && !hasAdditional) {
                    return this.createError({
                        message: 'Registration number is required when account number is provided'
                    });
                }

                // Non-USD: ABA is optional, no validation here
                if (!isUSD) return true;

                const hasSwift = !!swiftCode?.trim();

                // both filled: invalid
                if (hasAdditional && hasSwift) {
                    return this.createError({
                        message: 'Please input either SWIFT/BIC or ABA Routing Number (not both)'
                    });
                }

                if (!hasAdditional && !hasSwift) {
                    return this.createError({
                        message: 'Please input either SWIFT/BIC or ABA Routing Number'
                    });
                }

                return true;
            }),
        correspondentBankName: safeString().trim().nullable(),
        correspondentBankSwiftCode: safeString().trim().nullable(),
        intermediaryBankName: safeString().trim().nullable(),
        intermediaryBankSwiftCode: safeString().trim().nullable(),
        type: object({ label: safeString(), value: safeString() }).test(
            'is-required-when-whitelist-wallet',
            `Please select a wallet type`,
            function (type) {
                const { requestType } = this.parent;
                if (!isBankInfo) {
                    return type && type.value !== undefined;
                }
                return true;
            }
        ),
        vaspId: object({ label: safeString(), value: number() }).when('type', {
            is: (type) => type.value === WalletTypes.Regulated,
            then: object({ label: safeString(), value: number() })
                .test('valueNotUndefined', `Please select a VASP`, (vasp) => !!vasp.value)
                .required('VASP cannot be empty')
        }),
        tagMemo: safeString().trim().nullable(),
        networkId: object({ label: safeString(), value: number() }).test(
            'is-required-when-whitelist-wallet',
            `Please select a network`,
            function (network) {
                if (!isBankInfo) {
                    return network && network.value !== undefined;
                }
                return true;
            }
        ),
        assetsId: array(
            object({ label: safeString(), value: number() })
                .test('valueNotUndefined', `Please select an asset`, (asset) => !!asset.value)
                .required('Assets cannot be empty')
        ).when('requestType', {
            is: (requestType) => !isBankInfo,
            then: (schema) => schema.min(1, 'Assets cannot be empty')
        }),
        walletAddress: safeString()
            .trim()
            .when('requestType', {
                is: (requestType) => !isBankInfo,
                then: (schema) => schema.required('Please input a wallet address.')
            }),
        skipWalletAddressValidation: boolean(),
        proofOfOwnership: mixed().when([], {
            is: () => isBankInfo,
            then: mixed().required('Please upload a proof of ownership.'),
            otherwise: mixed().nullable()
        }),
        requestDescription: safeString().trim().nullable(),
        walletTermsAndConditions: boolean().when('requestType', {
            is: () => !isBankInfo,
            then: (schema) =>
                schema
                    .required('Please ensure you have agreed to the terms and conditions.')
                    .isTrue('Please ensure you have agreed to the terms and conditions.'),
            otherwise: (schema) => schema.optional()
        })
    });

export type PortalWhitelistRequestFormValues = InferType<ReturnType<typeof portalWhitelistRequestSchema>>;
export type PortalWhitelistRequestFormInput = Nullable<PortalWhitelistRequestFormValues>;
